import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { Check, Gift, Rocket, Info, ShieldCheck, Eye, EyeOff, Loader2, ArrowLeft, Facebook, Sparkles } from "lucide-react";

const BILLABLE = ["essentiel", "visibilite", "premium"];

export default function PartnerSignup() {
  const { planKey } = useParams();
  const navigate = useNavigate();
  const { user, register } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    business_name: "", contact_name: "", email: "", password: "",
    phone: "", city: "", category: "", offer_proposed: "", gdpr_consent: false,
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target?.value ?? e }));

  useEffect(() => {
    api.get("/partner-config").then((r) => setCfg(r.data)).catch(() => setCfg({ plans: [] }));
    api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {});
    api.get("/cities").then((r) => setCities(r.data || [])).catch(() => {});
  }, []);

  const plan = useMemo(() => (cfg?.plans || []).find((p) => p.key === planKey), [cfg, planKey]);
  const isBillable = BILLABLE.includes(planKey);

  useSeo({
    title: plan ? `Inscription ${plan.name} — PROXIreduc-28` : "Inscription partenaire — PROXIreduc-28",
    description: "Créez votre espace professionnel PROXIreduc-28 et choisissez votre mot de passe. Accès immédiat à votre tableau de bord.",
    canonicalPath: `/devenir-partenaire/inscription/${planKey || ""}`,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.gdpr_consent) { toast.error("Merci d'accepter les conditions."); return; }
    if ((f.password || "").length < 6 && !(user && user.role === "professional")) {
      toast.error("Votre mot de passe doit contenir au moins 6 caractères."); return;
    }
    setLoading(true);
    try {
      // Si l'utilisateur est déjà un professionnel connecté, on ne recrée pas de compte.
      const alreadyPro = user && user.role === "professional";
      if (!alreadyPro) {
        const parts = (f.contact_name || "").trim().split(" ");
        await register({
          email: f.email, password: f.password, role: "professional",
          first_name: parts[0] || f.contact_name || "", last_name: parts.slice(1).join(" "),
          city: f.city || "", business_name: f.business_name,
          business_category: f.category || "", business_phone: f.phone || "",
          offer_proposed: f.offer_proposed || "",
        });
      }
      if (planKey === "fondateur") {
        try {
          await api.post("/founders/activate", {});
          toast.success("Bienvenue ! Votre place Partenaire Fondateur est activée — 0 € pendant 12 mois.");
        } catch (actErr) {
          toast("Votre compte est créé. L'activation Fondateur n'a pas pu aboutir (places épuisées ?) — vous pourrez choisir une formule depuis votre espace.");
        }
        navigate("/pro?welcome=1");
        return;
      }
      if (isBillable) {
        const { data } = await api.post("/payments/checkout", {
          plan_key: planKey, origin_url: window.location.origin,
        });
        if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
        toast.error("Impossible de créer la session de paiement.");
        setLoading(false);
      } else {
        toast.success("Bienvenue ! Votre espace partenaire est prêt.");
        navigate("/pro?welcome=1");
      }
    } catch (err) {
      const msg = formatApiError(err);
      if (/déjà utilisé/i.test(msg)) {
        toast.error("Un compte existe déjà avec cet email. Connectez-vous pour continuer.");
        navigate(`/connexion?next=${encodeURIComponent(`/devenir-partenaire/inscription/${planKey}`)}`);
      } else {
        toast.error(msg);
      }
      setLoading(false);
    }
  };

  if (cfg && (!plan || plan.disabled || plan.active === false)) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="partner-signup-not-found">
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">Formule indisponible</h1>
        <p className="mt-2 text-slate-600">Cette formule n'est pas disponible à l'inscription pour le moment. Découvrez l'offre Partenaire Fondateur — 0 € pendant 12 mois.</p>
        <Link to="/devenir-partenaire"><Button className="mt-6 btn-shine text-white h-11 px-6">Voir l'offre Fondateur</Button></Link>
      </div>
    );
  }

  return (
    <div className="bg-[#FAF8F5] min-h-screen" data-testid="partner-signup-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <Link to="/devenir-partenaire" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="partner-signup-back">
          <ArrowLeft size={15} /> Retour aux formules
        </Link>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Récap formule */}
          <aside className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sticky top-6" data-testid="partner-signup-plan-summary">
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#E05638]">Formule choisie</div>
              <div className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{plan?.name || "…"}</div>
              {plan && (
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="font-heading text-3xl font-extrabold text-slate-900">{plan.price_display}</span>
                  <span className="text-slate-500 text-xs">{plan.period}</span>
                </div>
              )}
              {plan?.tagline && <p className="mt-2 text-sm text-slate-600">{plan.tagline}</p>}
              {planKey === "gratuit" && (
                <div className="ai-badge mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white text-[11px] font-extrabold uppercase tracking-wide w-fit shadow-sm" data-testid="ai-badge-gratuit">
                  <Sparkles size={13} className="ai-badge-spark"/> Propulsé par l'IA
                </div>
              )}
              {plan?.free_features ? (
                <>
                  <div className="mt-4">
                    <div className="text-[11px] font-extrabold uppercase tracking-wide text-emerald-700 flex items-center gap-1" data-testid="plan-free-header">
                      <Check size={12} /> Gratuit, pour toujours
                    </div>
                    <ul className="mt-2 space-y-1.5">
                      {plan.free_features.map((feat) => (
                        <li key={feat} className="flex items-start gap-2 text-sm text-slate-700">
                          <Check size={14} className="text-emerald-600 mt-0.5 shrink-0" /> <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {(plan.trial_features || []).length > 0 && (
                    <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="plan-trial-block">
                      <div className="text-[11px] font-extrabold uppercase tracking-wide text-amber-700 flex items-center gap-1">
                        <Gift size={12} /> Offert en essai 30 jours
                      </div>
                      <p className="mt-0.5 text-[11px] text-amber-700/80">Ensuite inclus dans une formule payante.</p>
                      <ul className="mt-2 space-y-1.5">
                        {plan.trial_features.map((feat) => (
                          <li key={feat} className="flex items-start gap-2 text-sm text-slate-700">
                            <Gift size={13} className="text-amber-600 mt-0.5 shrink-0" /> <span>{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {plan?.trial_note && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">
                      <Gift size={12} /> {plan.trial_note}
                    </div>
                  )}
                  <ul className="mt-4 space-y-1.5">
                    {(plan?.features || []).map((feat) => (
                      <li key={feat} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check size={14} className="text-emerald-600 mt-0.5 shrink-0" /> <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {plan?.facebook_note && (
                <div className="mt-3 text-[11px] italic text-slate-500 border-l-2 border-blue-200 pl-2 leading-tight flex gap-1.5">
                  <Facebook size={12} className="text-blue-600 shrink-0 mt-0.5" /> {plan.facebook_note}
                </div>
              )}
              <p className="mt-4 text-[11px] text-slate-500">TVA non applicable, art. 293 B du CGI.</p>
            </div>
          </aside>

          {/* Formulaire */}
          <main className="lg:col-span-3">
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7" data-testid="partner-signup-form">
              <h1 className="font-heading text-2xl font-extrabold text-slate-900">Créez votre espace partenaire</h1>
              <p className="mt-1 text-sm text-slate-500">
                {isBillable
                  ? "Choisissez votre mot de passe, puis réglez votre abonnement par carte. Vous accédez directement à votre espace."
                  : "Choisissez votre mot de passe et accédez immédiatement à votre espace professionnel."}
              </p>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2"><Label>Nom de l'entreprise *</Label><Input required value={f.business_name} onChange={set("business_name")} data-testid="ps-business" /></div>
                <div><Label>Nom du responsable *</Label><Input required value={f.contact_name} onChange={set("contact_name")} data-testid="ps-contact" /></div>
                <div><Label>Email *</Label><Input type="email" required value={f.email} onChange={set("email")} data-testid="ps-email" /></div>

                <div className="md:col-span-2">
                  <Label>Mot de passe * (min. 6 caractères)</Label>
                  <div className="mt-1 relative">
                    <Input type={showPwd ? "text" : "password"} required minLength={6} value={f.password} onChange={set("password")} className="pr-10" data-testid="ps-password" />
                    <button type="button" onClick={() => setShowPwd((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                            aria-label={showPwd ? "Masquer" : "Afficher"} data-testid="ps-password-toggle">
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div><Label>Téléphone</Label><Input value={f.phone} onChange={set("phone")} data-testid="ps-phone" /></div>
                <div>
                  <Label>Ville</Label>
                  <Select value={f.city || "none"} onValueChange={(v) => setF((s) => ({ ...s, city: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="ps-city"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Autre —</SelectItem>
                      {cities.map((c) => (<SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Label>Catégorie</Label>
                  <Select value={f.category || "none"} onValueChange={(v) => setF((s) => ({ ...s, category: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="ps-category"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— À préciser plus tard —</SelectItem>
                      {cats.map((c) => (<SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2"><Label>Offre proposée (optionnel)</Label><Input value={f.offer_proposed} onChange={set("offer_proposed")} placeholder="Ex : dessert offert pour 2 personnes" data-testid="ps-offer" /></div>
              </div>

              <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-[#E05638]/5 border border-[#E05638]/20" data-testid="ps-space-note">
                <Info size={16} className="text-[#E05638] shrink-0 mt-0.5" />
                <span className="text-xs text-slate-600 leading-relaxed">
                  Pas besoin de tout remplir maintenant : votre <strong>espace professionnel</strong> est créé immédiatement. Vous pourrez y compléter votre mini-site (adresse, horaires, catégorie, photos, réseaux sociaux, site internet…) et publier vos offres quand vous le souhaitez.
                </span>
              </div>

              <label className="mt-4 flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50 cursor-pointer hover:border-slate-300" data-testid="ps-gdpr-wrapper">
                <input type="checkbox" required checked={f.gdpr_consent}
                       onChange={(e) => setF((s) => ({ ...s, gdpr_consent: e.target.checked }))}
                       className="mt-1 w-4 h-4" data-testid="ps-gdpr" />
                <span className="text-xs text-slate-600 leading-relaxed">
                  J'accepte les conditions générales et la <Link to="/confidentialite" className="text-[#E05638] underline">politique de confidentialité</Link>.
                  Mes informations sont utilisées pour traiter mon adhésion et conservées 3 ans.
                </span>
              </label>

              <Button type="submit" disabled={loading || !f.gdpr_consent} className="w-full mt-4 btn-shine text-white h-12 text-base" data-testid="ps-submit">
                {loading ? <><Loader2 size={18} className="mr-2 animate-spin" /> {isBillable ? "Redirection…" : "Activation…"}</>
                  : <><Rocket size={18} className="mr-2" /> {isBillable ? "S'abonner et payer par carte" : planKey === "fondateur" ? "Activer ma place fondateur — 0 €" : "S'inscrire sur PROXIreduc-28"}</>}
              </Button>

              {isBillable && (
                <p className="mt-2 text-center text-[11px] text-slate-500 inline-flex w-full items-center justify-center gap-1.5" data-testid="ps-pay-hint">
                  <ShieldCheck size={13} /> Paiement sécurisé via Stripe · Sans engagement · Résiliation à tout moment
                </p>
              )}

              <div className="mt-3 text-center text-sm text-slate-500" data-testid="ps-login-link">
                Déjà un compte ? <Link to="/connexion" className="text-[#E05638] font-semibold">Se connecter</Link>
              </div>
            </form>
          </main>
        </div>
      </div>
    </div>
  );
}
